import React, { useEffect } from 'react'
import store from 'app/store'
import {
  HttpLink,
  split,
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  from,
  fromPromise,
  useLazyQuery
} from '@apollo/client'

import { createUploadLink } from 'apollo-upload-client'
import { getMainDefinition } from '@apollo/client/utilities'
import { WebSocketLink } from '@apollo/client/link/ws'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'
import { BrowserRouter as Router } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'app/rootReducer'
import { graphqlEndpoint, wsGraphqlEndpoint } from '../AppSettings'
import GlobalNavBar from 'components/features/common/globalnavbar/GlobalNavBar'
import MainContent from 'components/features/common/MainContent'
import {
  setAuthentication,
  setWithRefreshTokenResults,
  getEmptyAuthentication
} from 'reducers/authentication/authenticationSlice'
import { getRefreshTokens } from 'share/utils/utils'
import {
  GET_REFRESH_TOKEN_ALL,
  RefreshTokenResponseAll
} from 'share/queries/login'

import 'primeicons/primeicons.css'
import { PrimeReactProvider } from 'primereact/api'
import 'primeflex/primeflex.css'
import 'primereact/resources/primereact.css'
import 'primereact/resources/themes/lara-light-indigo/theme.css'

const httpLink = createUploadLink({
  uri: graphqlEndpoint,
  credentials: 'include'
})

const wsLink = new WebSocketLink({
  uri: wsGraphqlEndpoint,
  options: {
    reconnect: true,
    reconnectionAttempts: 5,
    connectionParams: () => {
      const state = store.getState()
      return {
        authorization: state.authentication.idToken
          ? `Bearer ${state.authentication.idToken}`
          : ''
      }
    }
  }
})

// Removed unused top-level authLink; per-request headers are set in useEffect below.

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  httpLink as unknown as HttpLink
)

const authMiddleware = new ApolloLink((operation, forward) => {
  const state = store.getState()
  const hasToken = !!state.authentication.idToken
  const isAuthenticated = state.authentication.isAuthenticated

  // Note: Using simple console.log to avoid circular imports
  console.log(`%c🔐 AUTH`, 'color: #EC4899; font-weight: bold', {
    operation: operation.operationName,
    hasToken,
    isAuthenticated
  })

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: state.authentication.idToken
        ? `Bearer ${state.authentication.idToken}`
        : ''
    }
  }))

  return forward(operation)
})

const errLink = onError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ graphQLErrors, networkError, operation, forward }): any => {
    console.log(
      'graphQLErrors: ',
      graphQLErrors,
      'networkError: ',
      networkError,
      'operation: ',
      operation
    )
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.extensions) {
          if (err.extensions.code) {
            if (err.extensions.code === 'AUTH_NOT_AUTHENTICATED') {
              console.log(
                'UNAUTHENTICATED in graphQLErrors error, call method to get new set of tokens'
              )
              const state = store.getState()
              console.log('my state is ', state)
              // only want to get the refresh tokens if the user is logged in.
              if (
                state.authentication.idToken &&
                state.authentication.isAuthenticated
              ) {
                console.log('call refresh tokens')
                return fromPromise(
                  getRefreshTokens().catch((error) => {
                    console.log('refreshTokens failure error', error)
                    // user is logged in so clear the authentication store before redirecting to login page
                    const authentication = getEmptyAuthentication()
                    console.log(authentication)
                    store.dispatch(setAuthentication(authentication))
                    window.location.replace('/login')
                    return
                  })
                )
                  .filter((value) => Boolean(value))
                  .flatMap((refreshTokenResult) => {
                    console.log('refreshTokens success are', refreshTokenResult)
                    const data =
                      refreshTokenResult &&
                      refreshTokenResult.data.authentication
                        .tokenFromRefreshToken
                    if (data) {
                      // check if idToken has changed
                      if (state.authentication.idToken === data.idToken) {
                        console.log(
                          'getRefreshTokens returned same expired idToken'
                        )
                      } else {
                        console.log('getRefreshToken returned a new idToken')
                      }
                      store.dispatch(setWithRefreshTokenResults(data))
                      console.log(
                        'forward operation called graphQLErrors error ',
                        operation,
                        forward
                      )
                    }
                    return forward(operation)
                  })
              }
            }
          }
        }
      }
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`)

      // Handle WebSocket connection errors specifically
      if (
        networkError.message?.includes('WebSocket') ||
        networkError.message?.includes('connection') ||
        networkError.name === 'ServerError'
      ) {
        console.error('WebSocket/Subscription connection error:', {
          name: networkError.name,
          message: networkError.message,
          operation: operation.operationName,
          variables: operation.variables,
          timestamp: new Date().toISOString(),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          statusCode: (networkError as any).statusCode,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          response: (networkError as any).response
        })
      }
    }
  }
)

export const client = new ApolloClient({
  link: from([authMiddleware, errLink, splitLink]),
  cache: new InMemoryCache()
})

function App(): JSX.Element | null {
  const dispatch = useDispatch()
  const authentication = useSelector((state: RootState) => state.authentication)

  const [GetAuthFromRefreshToken, { data, error, loading }] =
    useLazyQuery<RefreshTokenResponseAll>(GET_REFRESH_TOKEN_ALL, {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      client
    })

  // the onbeforeunload should only fire on a hard refresh such as F5
  useEffect(() => {
    window.onbeforeunload = function () {
      const pathname = window.location.pathname
      const sessionStor = window.sessionStorage
      console.log('onbeforeunload refreshPath', pathname)
      sessionStor.setItem('refreshPath', pathname)

      return null
    }
    return () => {
      window.onbeforeunload = null
    }
  }, [])

  useEffect(() => {
    GetAuthFromRefreshToken()
  }, [])

  useEffect(() => {
    if (data && !error && !loading) {
      const authData = {
        ...data.authentication.tokenFromRefreshToken,
        isAuthenticated: true
      }
      dispatch(setAuthentication(authData))
    }
  }, [data, error, loading])

  useEffect(() => {
    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: authentication.idToken
            ? `Bearer ${authentication.idToken}`
            : '',
          Access_Token: authentication.accessToken
            ? authentication.accessToken
            : '',
          'Access-Control-Allow-Credentials': true
        }
      }
    })

    const links = from([errLink, authLink, authMiddleware, splitLink])
    client.setLink(links)

    //client.setLink(authLink.concat(httpLink))
  }, [authentication, client])

  if (!data && loading) {
    return (
      <div>
        <span className='mdi mdi-autorenew mdi-spin mdi-18px' /> Loading
      </div>
    )
  }

  return (
    <ApolloProvider client={client}>
      <PrimeReactProvider>
        <Router>
          <GlobalNavBar />
          <MainContent />
        </Router>
      </PrimeReactProvider>
    </ApolloProvider>
  )
}

export default App
